import {
  AIApartmentResult,
  AIChatApartmentSearchResult,
  AIChatNumericRange,
  AIChatSearchApartmentsArgs,
  AIChatToolApartment,
} from 'src/typings/ai-chat';
import {
  IFilterAllData,
  ListOfKeysFilterAllData,
  PlacementShowModule,
} from 'src/typings/filter';

const DEFAULT_LIMIT = 20;
const UNKNOWN_APARTMENT_NUMERATION = 'Unnamed apartment';

const numericRangeKeys = [
  'rooms',
  'bedrooms',
  'bathrooms',
  'totalArea',
  'totalPrice',
  'floor',
] as const;

type NumericRangeKey = typeof numericRangeKeys[number];

// These are the broad bounds the model uses as defaults, not user intent.
const universalRangeMax: Record<NumericRangeKey, number> = {
  rooms: 100,
  bedrooms: 100,
  bathrooms: 100,
  totalArea: 1_000_000,
  totalPrice: 100_000_000,
  floor: 100,
};

const modelDefaultRangeMax: Record<NumericRangeKey, number> = {
  rooms: 10,
  bedrooms: 10,
  bathrooms: 10,
  totalArea: 1_000,
  totalPrice: 100_000_000,
  floor: 100,
};

const floorRequestPattern = /(?:\bfloors?\b|повер(?:х|с)\w*|этаж\w*)/i;
const otherCriterionPattern = /(?:\brooms?\b|\bbedrooms?\b|\bbathrooms?\b|\barea\b|\bsize\b|\bsq\.?\s*(?:ft|m)\b|\bprice\b|\bbudget\b|\bcost\b|кімнат\w*|спалень\w*|санвуз\w*|ванн\w*|площ\w*|цін\w*|бюджет\w*|(?:^|[^А-Яа-яЇїІіЄєҐґ])варт[А-Яа-яЇїІіЄєҐґ]*|грн\b|aed\b|usd\b|eur\b|m²|m2)/i;
const numericCriterionPatterns: Record<NumericRangeKey, RegExp> = {
  rooms: /(?:\brooms?\b|кімнат\w*|студ\w*)/i,
  bedrooms: /(?:\bbedrooms?\b|спалень\w*)/i,
  bathrooms: /(?:\bbathrooms?\b|санвуз\w*|ванн\w*)/i,
  totalArea: /(?:\barea\b|\bsize\b|\bsq\.?\s*(?:ft|m)\b|площ\w*|m²|m2)/i,
  totalPrice: /(?:\bprice\b|\bbudget\b|\bcost\b|цін\w*|бюджет\w*|(?:^|[^А-Яа-яЇїІіЄєҐґ])варт[А-Яа-яЇїІіЄєҐґ]*|грн\b|aed\b|usd\b|eur\b)/i,
  floor: floorRequestPattern,
};

const isEmptyRange = (range: AIChatNumericRange | undefined) => (
  !!range && range.min === undefined && range.max === undefined
);

const isUniversalRange = (
  range: AIChatNumericRange | undefined,
  max: number,
) => (
  !!range
  && (range.min === undefined || range.min <= 0)
  && (range.max === undefined || range.max >= max)
);

const isZeroRange = (range: AIChatNumericRange | undefined) => (
  !!range && range.min === 0 && range.max === 0
);

const isModelDefaultRange = (
  range: AIChatNumericRange | undefined,
  key: NumericRangeKey,
) => (
  isUniversalRange(range, universalRangeMax[key])
  || isUniversalRange(range, modelDefaultRangeMax[key])
  || isZeroRange(range)
);

/**
 * Models sometimes serialize omitted filters as very broad ranges. Treat
 * those ranges as unset so unknown catalog values (for example null
 * bathrooms) do not make an otherwise valid placement disappear.
 */
export const normalizeAIChatSearchArguments = (
  args: AIChatSearchApartmentsArgs,
  userText = '',
): AIChatSearchApartmentsArgs => {
  const normalizedArgs = { ...args };
  const floorOnlyRequest = floorRequestPattern.test(userText)
    && !otherCriterionPattern.test(userText);

  if (floorOnlyRequest) {
    numericRangeKeys.forEach((key) => {
      if (key !== 'floor') delete normalizedArgs[key];
    });
    delete normalizedArgs.houses;
    delete normalizedArgs.sections;
    delete normalizedArgs.planningTypes;
    delete normalizedArgs.features;
    return normalizedArgs;
  }

  numericRangeKeys.forEach((key) => {
    if (
      isEmptyRange(normalizedArgs[key])
      || (!numericCriterionPatterns[key].test(userText) && isModelDefaultRange(normalizedArgs[key], key))
    ) {
      delete normalizedArgs[key];
    }
  });

  return normalizedArgs;
};

export const parseAIChatNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;

  const normalized = value.replace(/[^\d,.-]/g, '');
  if (!/\d/.test(normalized)) return null;
  const separatorIndex = Math.max(normalized.lastIndexOf(','), normalized.lastIndexOf('.'));
  let numeric = normalized;
  if (separatorIndex >= 0) {
    const fractionLength = normalized.length - separatorIndex - 1;
    numeric = fractionLength > 0 && fractionLength <= 2
      ? `${normalized.slice(0, separatorIndex).replace(/[,.]/g, '')}.${normalized.slice(separatorIndex + 1)}`
      : normalized.replace(/[,.]/g, '');
  }

  const parsed = Number(numeric);
  return Number.isFinite(parsed) ? parsed : null;
};

const matchesRange = (value: number | null, range: AIChatNumericRange | undefined) => {
  if (!range) return true;
  if (value === null) return false;
  if (range.min !== undefined && value < range.min) return false;
  if (range.max !== undefined && value > range.max) return false;
  return true;
};

const compactApartment = (
  candidate: IFilterAllData,
  primaryPriceCurrency: string | null,
): AIChatToolApartment => {
  const totalPrice = parseAIChatNumber(
    candidate[ListOfKeysFilterAllData.placementTotalPrice],
  );
  return {
    id: String(candidate[ListOfKeysFilterAllData.id]),
    numeration: candidate[ListOfKeysFilterAllData.placementNumeration] || null,
    placementType: candidate[ListOfKeysFilterAllData.placementType] || null,
    rooms: parseAIChatNumber(candidate[ListOfKeysFilterAllData.placementRooms]),
    bedrooms: parseAIChatNumber(candidate[ListOfKeysFilterAllData.placementBedrooms]),
    bathrooms: parseAIChatNumber(candidate[ListOfKeysFilterAllData.placementBathrooms]),
    totalArea: parseAIChatNumber(candidate[ListOfKeysFilterAllData.placementTotalArea]),
    totalPrice,
    totalPriceCurrency: totalPrice === null ? null : primaryPriceCurrency,
    floors: (candidate[ListOfKeysFilterAllData.placementFloor] || [])
      .map(parseAIChatNumber)
      .filter((floor): floor is number => floor !== null),
    house: candidate[ListOfKeysFilterAllData.placementHouse] || null,
    section: candidate[ListOfKeysFilterAllData.placementSection] || null,
    planningType: candidate[ListOfKeysFilterAllData.placementPlanningType] || null,
    features: [
      ...(candidate[ListOfKeysFilterAllData.placementAdditionalAdvantages] || []),
      candidate[ListOfKeysFilterAllData.placementAdditionalCondition],
    ].filter((feature): feature is string => typeof feature === 'string' && feature.length > 0),
  };
};

const matchesNumericFilters = (apartment: AIChatToolApartment, args: AIChatSearchApartmentsArgs) => {
  if (!matchesRange(apartment.rooms, args.rooms)) return false;
  if (!matchesRange(apartment.bedrooms, args.bedrooms)) return false;
  if (!matchesRange(apartment.bathrooms, args.bathrooms)) return false;
  if (!matchesRange(apartment.totalArea, args.totalArea)) return false;
  if (!matchesRange(apartment.totalPrice, args.totalPrice)) return false;
  if (!args.floor && !args.excludedFloors?.length) return true;

  const excluded = new Set(args.excludedFloors || []);
  return apartment.floors.some((floor) => !excluded.has(floor) && matchesRange(floor, args.floor));
};

const normalized = (value: string) => value.trim().toLocaleLowerCase();

const uniqueCatalogValues = (values: Array<string | null | undefined>): Map<string, string> => (
  new Map(values
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map((value) => [normalized(value), value.trim()]))
);

const filterCatalogValues = (
  values: string[] | undefined,
  catalogValues: Map<string, string>,
): string[] | undefined => {
  if (!Array.isArray(values)) return undefined;
  return values
    .map((value) => catalogValues.get(normalized(value)))
    .filter((value): value is string => Boolean(value));
};

/** Keep model-controlled text in progress details limited to known catalog labels. */
export const sanitizeAIChatSearchArgumentsForDisplay = (
  args: AIChatSearchApartmentsArgs,
  candidates: IFilterAllData[],
): AIChatSearchApartmentsArgs => {
  const houses = uniqueCatalogValues(candidates.map((candidate) => candidate[ListOfKeysFilterAllData.placementHouse]));
  const sections = uniqueCatalogValues(candidates.map((candidate) => candidate[ListOfKeysFilterAllData.placementSection]));
  const planningTypes = uniqueCatalogValues(candidates.map((candidate) => candidate[ListOfKeysFilterAllData.placementPlanningType]));
  const features = uniqueCatalogValues(candidates.flatMap((candidate) => [
    ...(candidate[ListOfKeysFilterAllData.placementAdditionalAdvantages] || []),
    candidate[ListOfKeysFilterAllData.placementAdditionalCondition],
  ]));
  const sanitized = { ...args };
  const placementTypes = uniqueCatalogValues(candidates.map((candidate) => candidate[ListOfKeysFilterAllData.placementType]));
  if (args.houses !== undefined) sanitized.houses = filterCatalogValues(args.houses, houses);
  if (args.sections !== undefined) sanitized.sections = filterCatalogValues(args.sections, sections);
  if (args.planningTypes !== undefined) sanitized.planningTypes = filterCatalogValues(args.planningTypes, planningTypes);
  if (args.placementTypes !== undefined) sanitized.placementTypes = filterCatalogValues(args.placementTypes, placementTypes);
  if (args.features !== undefined) sanitized.features = filterCatalogValues(args.features, features);
  return sanitized;
};

const matchesAnyText = (value: string | null, options: string[] | undefined) => {
  if (!options?.length) return true;
  if (!value) return false;
  const candidate = normalized(value);
  return options.some((option) => candidate.includes(normalized(option)));
};

const matchesTextFilters = (apartment: AIChatToolApartment, args: AIChatSearchApartmentsArgs) => {
  if (!matchesAnyText(apartment.house, args.houses)) return false;
  if (!matchesAnyText(apartment.section, args.sections)) return false;
  if (!matchesAnyText(apartment.planningType, args.planningTypes)) return false;
  if (!matchesAnyText(apartment.placementType, args.placementTypes)) return false;
  if (!args.features?.length) return true;

  const availableFeatures = apartment.features.map(normalized);
  return args.features.every((feature) => (
    availableFeatures.some((available) => available.includes(normalized(feature)))
  ));
};

const getSortValue = (
  apartment: AIChatToolApartment,
  field: NonNullable<AIChatSearchApartmentsArgs['sort']>['field'],
) => {
  if (field === 'floor') return apartment.floors[0] ?? null;
  return apartment[field];
};

const sortApartments = (
  apartments: AIChatToolApartment[],
  sort: AIChatSearchApartmentsArgs['sort'],
) => {
  if (!sort) return apartments;
  const direction = sort.direction === 'asc' ? 1 : -1;
  return apartments
    .map((apartment, sourceIndex) => ({ apartment, sourceIndex }))
    .sort((left, right) => {
      const leftValue = getSortValue(left.apartment, sort.field);
      const rightValue = getSortValue(right.apartment, sort.field);
      if (leftValue === null && rightValue === null) return left.sourceIndex - right.sourceIndex;
      if (leftValue === null) return 1;
      if (rightValue === null) return -1;
      return (leftValue - rightValue) * direction || left.sourceIndex - right.sourceIndex;
    })
    .map(({ apartment }) => apartment);
};

const snapshotApartment = (candidate: IFilterAllData): AIApartmentResult => {
  const id = String(candidate[ListOfKeysFilterAllData.id]);
  const gallery = candidate[ListOfKeysFilterAllData.placementGallery];
  return {
    id,
    numeration: candidate[ListOfKeysFilterAllData.placementNumeration] || UNKNOWN_APARTMENT_NUMERATION,
    placementType: candidate[ListOfKeysFilterAllData.placementType] || null,
    rooms: candidate[ListOfKeysFilterAllData.placementRooms] || null,
    totalArea: candidate[ListOfKeysFilterAllData.placementTotalArea] || null,
    floor: candidate[ListOfKeysFilterAllData.placementFloor]?.[0] || null,
    building: candidate[ListOfKeysFilterAllData.placementHouse] || null,
    price: candidate[ListOfKeysFilterAllData.placementTotalPrice] || null,
    imageUrl: gallery?.[0] || candidate[ListOfKeysFilterAllData.mainFilterImg] || null,
  };
};

export const selectRecommendedApartmentSnapshots = (
  recommendedIds: Array<string | number>,
  candidates: IFilterAllData[],
  limit = 6,
): AIApartmentResult[] => {
  const byId = new Map(candidates.map((candidate) => (
    [String(candidate[ListOfKeysFilterAllData.id]), candidate]
  )));
  const seen = new Set<string>();

  return recommendedIds.reduce((snapshots, recommendedId) => {
    const id = String(recommendedId);
    const candidate = byId.get(id);
    if (!candidate || seen.has(id) || snapshots.length >= limit) return snapshots;
    seen.add(id);
    snapshots.push(snapshotApartment(candidate));
    return snapshots;
  }, [] as AIApartmentResult[]);
};

export const searchApartments = (
  args: AIChatSearchApartmentsArgs,
  candidates: IFilterAllData[],
  primaryPriceCurrency: string | null,
  userText = '',
): AIChatApartmentSearchResult => {
  const normalizedArgs = normalizeAIChatSearchArguments(args, userText);
  const visibleApartments = candidates
    .filter((candidate) => candidate[ListOfKeysFilterAllData.placementStatusShowInModule] !== PlacementShowModule.hide)
    .map((candidate) => compactApartment(candidate, primaryPriceCurrency));
  const currencies = primaryPriceCurrency
    && visibleApartments.some(({ totalPrice }) => totalPrice !== null)
    ? [primaryPriceCurrency]
    : [];
  const matches = sortApartments(visibleApartments
    .filter((candidate) => matchesNumericFilters(candidate, normalizedArgs))
    .filter((candidate) => matchesTextFilters(candidate, normalizedArgs)), normalizedArgs.sort);
  const limit = Math.min(Math.max(Math.trunc(normalizedArgs.limit || DEFAULT_LIMIT), 1), DEFAULT_LIMIT);
  const apartments = matches.slice(0, limit);

  return {
    total: matches.length,
    currencies,
    apartments,
    apartmentIds: apartments.map(({ id }) => id),
  };
};
